'use client';

import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import './styles.css';

import { cn } from '@/lib/utils';

import Button from '@/components/atoms/button';
import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import { BlockReference } from '@/components/cms/types/block';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { ImageStrapi } from '@/components/cms/types/image';
import SocialWidgetItem from '@/components/organisms/socialWidget/SocialWidgetItem';

import { LinkButtonSBProps } from '@/components/atoms/linkButton/LinkSB';
import { PopoverProps } from '@/components/atoms/popover';
import { ToggleCMSProps } from '@/components/atoms/toggle/useCMSToggle';
import LiveChat from './LiveChat';

export type SocialWidgetEntry = {
  icon: ImageSBProps;
  link: LinkButtonSBProps;
  tab?: ToggleCMSProps;
  popup?: ImageSBProps[];
};

type Position = 'right' | 'left';
export type SocialWidgetStrapiProps = {
  unfolded: boolean;
  position: Position;
  items: SocialWidgetEntry[];
  actionFloatingButton: BlockReference<ImageStrapi>;
  actionFloatingCloseButton: BlockReference<ImageStrapi>;
};

const getOffsetForPosition = (position: Position): `${Position}-${number}` =>
  position === 'left' ? 'left-10' : 'right-10';

export const SocialWidgetStrapi = (
  props: BlockComponentModel<SocialWidgetStrapiProps>
) => {
  const {
    actionFloatingButton,
    actionFloatingCloseButton,
    items,
    position,
    unfolded,
  } = props.block;

  const [isUnfolded, setIsUnfolded] = useState<boolean>(unfolded ?? false);
  const [liveChatReady, setLiveChatReady] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!liveChatReady) return;

    setLiveChatVisibile(props.block.unfolded);
  }, [unfolded, liveChatReady, props.block.unfolded]);

  const setLiveChatVisibile = (val: boolean) => {
    const container = document.querySelector(
      '#chat-widget-container'
    ) as HTMLDivElement;

    if (val) {
      container.style.visibility = 'visible';
      container.style.opacity = '1';
    } else {
      container.style.visibility = 'hidden';
      container.style.opacity = '0';
    }
  };

  const handleLiveChatReady = () => {
    setLiveChatReady(true);
    const container = document.querySelector('#chat-widget-container');
    const minimizedContainer = document.querySelector('#chat-widget-minimized');

    if (container)
      container.classList.add(
        'custom-hack-class-for-widget',
        `custom-hack-class-for-widget-${props.block.position}`
      );

    if (minimizedContainer)
      minimizedContainer.classList.add(
        'custom-hack-class-for-widget',
        `custom-hack-class-for-widget-${props.block.position}`
      );
  };

  const toggleUnfolded = () => {
    setLiveChatVisibile(!isUnfolded);
    setIsUnfolded((prev) => !prev);
    setActiveTabIndex(null);
  };

  const popupProps: Partial<PopoverProps> = {
    position: props.block.position === 'right' ? 'left' : 'right',
  };

  function handleSelect(index: number) {
    index !== activeTabIndex
      ? setActiveTabIndex(index)
      : setActiveTabIndex(null);
  }

  return (
    <>
      <motion.div
        className={cn(
          'bg-neutral-N00 hover:border-primary-P60 fixed bottom-[180px] z-50 flex  flex-col rounded-full p-3',
          getOffsetForPosition(position),
          'shadow-popup',
          liveChatReady ? 'visible' : 'hidden'
        )}
        id='social-button-container'
        animate={!isUnfolded ? 'folded' : 'unfolded'}
        initial='folded'
        variants={{
          folded: { opacity: 0, y: 20 },
          unfolded: { opacity: 1, y: 0 },
        }}
        data-tracker-section='social-widget'
      >
        {items?.map((item, index) => {
          return (
            <div key={index}>
              <div
                className={cn(
                  'border-neutral-N20 hover:border-primary-P50 flex h-10 w-10 items-center justify-center rounded-full border-[1px] p-2',
                  activeTabIndex === index && 'border-primary-P99',
                  index !== 0 && 'mt-2'
                )}
              >
                <SocialWidgetItem
                  tab={item.tab}
                  icon={item.icon}
                  key={index}
                  link={item.link}
                  handleSelectCallback={() => handleSelect(index)}
                  popup={item.popup}
                  PopupProps={popupProps}
                  data-tracker-context={`button-${index}`}
                  data-tracker-component={`social-widget`}
                />
              </div>
            </div>
          );
        })}
      </motion.div>

      <Button
        onClick={toggleUnfolded}
        className={cn(
          'bg-brand-B99 border-neutral-N20 fixed bottom-8 z-50 h-[60px] w-[60px] rounded-full',
          getOffsetForPosition(position),
          liveChatReady ? 'visible' : 'hidden'
        )}
      >
        <ImageSB
          block={{
            image: isUnfolded
              ? actionFloatingCloseButton
              : actionFloatingButton,
          }}
        />
      </Button>
      <LiveChat onReady={handleLiveChatReady} />
    </>
  );
};

export default SocialWidgetStrapi;
